import { useState, useEffect } from "react";
import axios from "axios";
import { api_url } from "../config/config";
import { Helmet } from "react-helmet";

const plusPattern = `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`;

const formatDate = (dateString) =>
  new Date(dateString).toLocaleString("en-IN", {
    year:   "numeric",
    month:  "short",
    day:    "numeric",
    hour:   "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const formatAmount = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style:                 "currency",
    currency:              "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const STATUS = {
  captured: { label: "Captured", cls: "bg-[#eaf4f3] text-[#1e4a46] border-[#d4ecea]", dot: "bg-[#5fbdb3]" },
  failed:   { label: "Failed",   cls: "bg-red-50 text-red-600 border-red-200",         dot: "bg-red-500"   },
};

const getStatus = (raw = "") => {
  const key = raw.toLowerCase();
  return STATUS[key] || { label: raw.charAt(0).toUpperCase() + raw.slice(1), cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-400" };
};

const METHOD_ICON = {
  card:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  upi:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  netbanking: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="10" width="18" height="11" rx="1"/><line x1="3" y1="14" x2="21" y2="14"/><path d="M12 3L2 10h20L12 3z"/></svg>,
  wallet: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12v4"/><path d="M4 6v12a2 2 0 0 0 2 2h14v-4"/><circle cx="17" cy="14" r="1"/></svg>,
};

const getMethodIcon = (method = "") => METHOD_ICON[method.toLowerCase()] || (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

export default function MyTransactions() {
  const [transactions,   setTransactions]   = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [visibleCount,   setVisibleCount]   = useState(10);
  const [statusFilterUI, setStatusFilterUI] = useState("");

  const statusOptions = [
    { value: "",         label: "All Transactions" },
    { value: "captured", label: "Captured"         },
    { value: "failed",   label: "Failed"            },
  ];

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const url   = statusFilterUI
        ? `${api_url}/api/user-payments?status=${statusFilterUI}`
        : `${api_url}/api/user-payments`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.status === "success") {
        let txns = response.data.data;
        if (statusFilterUI) {
          txns = txns.filter(t => t.status.toLowerCase() === statusFilterUI.toLowerCase());
        }
        setTransactions(txns);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setError("Failed to load transactions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, [statusFilterUI]);

  const visible   = transactions.slice(0, visibleCount);
  const hasMore   = visibleCount < transactions.length;
  const hasLess   = visibleCount > 10 && transactions.length > 10;

  /* ── Summary stats ── */
  const totalCaptured = transactions.filter(t => t.status.toLowerCase() === "captured").reduce((s, t) => s + (t.amount || 0), 0);
  const capturedCount = transactions.filter(t => t.status.toLowerCase() === "captured").length;
  const failedCount   = transactions.filter(t => t.status.toLowerCase() === "failed").length;

  return (
    <>
      <Helmet>
        <title>Transaction History | MediSYS Pharmacy</title>
        <meta name="description" content="View your MediSYS Pharmacy transaction history and payment details." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-[#f8fafa] font-poppins">

        {/* ── Hero strip ── */}
        <div className="relative bg-[#162c2a] overflow-hidden px-5 sm:px-8 lg:px-14 pt-8 pb-12">
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{ backgroundImage: plusPattern, backgroundRepeat: "repeat", backgroundSize: "40px 40px" }} />
          <div className="absolute -top-10 right-0 w-72 h-72 rounded-full bg-[#5fbdb3]/10 blur-3xl pointer-events-none" />

          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="w-5 h-[2px] bg-[#ff601e] rounded-full" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5fbdb3]">Account</span>
              </div>
              <h1 className="font-bold text-[24px] sm:text-[30px] text-white">Transaction History</h1>
              {!loading && (
                <p className="text-[13px] text-white/50 mt-1">{transactions.length} transaction{transactions.length !== 1 ? "s" : ""} found</p>
              )}
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-semibold text-white/60">Filter:</span>
              <div className="relative">
                <select value={statusFilterUI} onChange={e => setStatusFilterUI(e.target.value)}
                  className="appearance-none bg-white/[0.1] border border-white/[0.15] text-white text-[12px] font-semibold rounded-xl pl-4 pr-8 h-9 outline-none focus:bg-white/[0.15] transition-all cursor-pointer">
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value} className="text-[#1a2625] bg-white">{opt.label}</option>
                  ))}
                </select>
                <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-8 lg:px-14 py-8 -mt-4">

          {/* ── Summary cards ── */}
          {!loading && transactions.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                {
                  label: "Total Spent",
                  value: formatAmount(totalCaptured),
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                  ),
                  accent: "border-[#5fbdb3]/30 bg-[#eaf4f3]",
                },
                {
                  label: "Successful",
                  value: `${capturedCount} transaction${capturedCount !== 1 ? "s" : ""}`,
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e4a46" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ),
                  accent: "border-[#d4ecea] bg-[#eaf4f3]",
                },
                {
                  label: "Failed",
                  value: `${failedCount} transaction${failedCount !== 1 ? "s" : ""}`,
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  ),
                  accent: "border-red-100 bg-red-50",
                },
              ].map((card) => (
                <div key={card.label} className={`bg-white rounded-2xl border ${card.accent} shadow-[0_2px_10px_rgba(22,44,42,0.06)] p-5 flex items-center gap-4`}>
                  <div className={`w-10 h-10 rounded-xl ${card.accent} border flex items-center justify-center flex-shrink-0`}>
                    {card.icon}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8faba9]">{card.label}</p>
                    <p className="font-bold text-[16px] sm:text-[17px] text-[#1e4a46] leading-tight mt-0.5">{card.value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Loading ── */}
          {loading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#e2eaea] p-4 animate-pulse flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#e2eaea] flex-shrink-0" />
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="h-3 w-40 bg-[#e2eaea] rounded-full" />
                    <div className="h-3 w-24 bg-[#e2eaea] rounded-full" />
                  </div>
                  <div className="h-6 w-20 bg-[#e2eaea] rounded-full" />
                  <div className="h-6 w-16 bg-[#e2eaea] rounded-full" />
                </div>
              ))}
            </div>

          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <p className="font-semibold text-[15px] text-[#374747]">{error}</p>
              <button onClick={fetchTransactions}
                className="text-[13px] font-semibold text-[#1e4a46] hover:text-[#ff601e] transition-colors">
                Try again
              </button>
            </div>

          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <p className="font-semibold text-[15px] text-[#374747]">No transactions found</p>
              <p className="text-[13px] text-[#8faba9]">
                {statusFilterUI ? `No ${statusFilterUI} transactions yet.` : "You haven't made any payments yet."}
              </p>
            </div>

          ) : (
            <>
              {/* ── Desktop table ── */}
              <div className="hidden sm:block bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.07)] overflow-hidden">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="bg-[#1e4a46]">
                      {["Order ID","Method","Date & Time","Amount","Status"].map((h) => (
                        <th key={h} className="text-left px-5 py-3.5 text-[12px] font-semibold text-white/85 whitespace-nowrap first:rounded-tl-2xl last:rounded-tr-2xl">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((txn, idx) => {
                      const st = getStatus(txn.status);
                      return (
                        <tr key={txn.id}
                          className={`border-t border-[#f1f5f5] hover:bg-[#f8fafa] transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-[#fafcfc]"}`}>
                          {/* Order ID */}
                          <td className="px-5 py-3.5">
                            <span className="font-mono text-[12px] text-[#374747] bg-[#f1f5f5] px-2 py-0.5 rounded-lg">
                              {txn.order_id || "—"}
                            </span>
                          </td>
                          {/* Method */}
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[#8faba9]">{getMethodIcon(txn.method)}</span>
                              <span className="capitalize font-medium text-[#374747]">{txn.method || "—"}</span>
                            </div>
                          </td>
                          {/* Date */}
                          <td className="px-5 py-3.5 text-[#607d7b] whitespace-nowrap">
                            {formatDate(txn.captured_at || txn.created_at)}
                          </td>
                          {/* Amount */}
                          <td className="px-5 py-3.5">
                            <span className="font-bold text-[14px] text-[#1e4a46]">
                              {formatAmount(txn.amount)}
                            </span>
                          </td>
                          {/* Status */}
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                              {st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Mobile cards ── */}
              <div className="flex sm:hidden flex-col gap-3">
                {visible.map((txn) => {
                  const st = getStatus(txn.status);
                  return (
                    <div key={txn.id} className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_10px_rgba(22,44,42,0.06)] p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-semibold text-[#8faba9] uppercase tracking-wide mb-1">Order ID</p>
                          <span className="font-mono text-[11px] text-[#374747] bg-[#f1f5f5] px-2 py-0.5 rounded-lg break-all">
                            {txn.order_id || "—"}
                          </span>
                        </div>
                        <span className={`flex-shrink-0 inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {st.label}
                        </span>
                      </div>
                      <div className="h-px bg-[#f1f5f5] mb-3" />
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <p className="text-[10px] text-[#8faba9] mb-0.5">Method</p>
                          <div className="flex items-center gap-1">
                            <span className="text-[#8faba9]">{getMethodIcon(txn.method)}</span>
                            <span className="text-[12px] font-semibold text-[#374747] capitalize">{txn.method || "—"}</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#8faba9] mb-0.5">Amount</p>
                          <p className="text-[13px] font-bold text-[#1e4a46]">{formatAmount(txn.amount)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#8faba9] mb-0.5">Date</p>
                          <p className="text-[11px] text-[#607d7b] leading-tight">{formatDate(txn.captured_at || txn.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── View more / less ── */}
              {(hasMore || hasLess) && (
                <div className="flex justify-center gap-3 mt-8">
                  {hasMore && (
                    <button onClick={() => setVisibleCount(c => c + 10)}
                      className="flex items-center gap-2 h-10 px-6 rounded-xl bg-[#162c2a] hover:bg-[#1e4a46] text-white text-[13px] font-semibold transition-all duration-200 shadow-[0_2px_8px_rgba(22,44,42,0.25)]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                      View More
                    </button>
                  )}
                  {hasLess && (
                    <button onClick={() => { setVisibleCount(10); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className="flex items-center gap-2 h-10 px-6 rounded-xl bg-white border border-[#e2eaea] hover:border-[#5fbdb3] hover:bg-[#eaf4f3] text-[#374747] text-[13px] font-semibold transition-all duration-200">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
                      View Less
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}